import React, { useState, useEffect, useRef } from 'react';
import { useSocketContext } from '../contexts/SocketContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ChatMessage {
  id: number;
  userId: number;
  username: string;
  message: string;
  createdAt: string;
}

const GlobalChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { socket } = useSocketContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  // Listen for new chat messages
  useEffect(() => {
    if (!socket) return;

    const handleChatMessage = (data: ChatMessage) => {
      setMessages(prev => [...prev, data]);
      
      // Show browser notification if chat is closed and message is from another user
      // Note: In a real app, you might want to check if user is on current tab
      if (!isOpen && 'Notification' in window && Notification.permission === 'granted') {
        // Get current user ID from session if available
        const notification = new Notification(`Neue Chat-Nachricht von ${data.username}`, {
          body: data.message.length > 100 ? data.message.substring(0, 100) + '...' : data.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `chat-${data.id}`,
          requireInteraction: false
        });

        notification.onclick = () => {
          window.focus();
          setIsOpen(true);
          notification.close();
        };

        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    };

    const handleChatError = (data: { message: string }) => {
      alert(data.message);
    };

    socket.on('chat-message', handleChatMessage);
    socket.on('chat-error', handleChatError);

    return () => {
      socket.off('chat-message', handleChatMessage);
      socket.off('chat-error', handleChatError);
    };
  }, [socket, isOpen]);

  const loadChatHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/chat/messages?limit=50`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (!socket || !newMessage.trim()) return;

    socket.emit('chat-message', {
      message: newMessage.trim()
    });

    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat toggle button - always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 z-40 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-full shadow-lg transition duration-200 flex items-center gap-2 min-h-[44px] ${
          isOpen ? 'bg-purple-700' : ''
        }`}
        style={{ zIndex: 40 }}
        aria-label={isOpen ? 'Chat schließen' : 'Chat öffnen'}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        {!isOpen && messages.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {messages.length > 99 ? '99+' : messages.length}
          </span>
        )}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className="fixed bottom-20 right-4 z-40 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border border-gray-300 flex flex-col max-h-[500px] min-h-[400px]"
        >
          {/* Chat header */}
          <div className="bg-purple-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <h3 className="font-semibold text-lg">Globaler Chat</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200 transition"
              aria-label="Chat schließen"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Messages container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
            {loading ? (
              <div className="text-center text-gray-500 py-4">Lädt...</div>
            ) : messages.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                Noch keine Nachrichten. Schreibe die erste!
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className="bg-white rounded-lg p-2 shadow-sm border border-gray-200"
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-semibold text-sm text-purple-600">
                      {msg.username}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(msg.createdAt).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 break-words">{msg.message}</p>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message input */}
          <div className="border-t border-gray-300 p-3 bg-white rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nachricht eingeben..."
                maxLength={500}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm min-h-[44px]"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px]"
              >
                Senden
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalChat;

